import { useEffect, useRef } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { UseHook } from 'src/@types/common';

const displayCache = new Map<string, { id: number; name: string }>();

type FilterDictionaryFieldProps = {
  /** RHF Dictionary component (e.g. RHFProductCategory) — функция, возвращающая JSX */
  DictionaryComponent: React.ComponentType<{ name: string; label: string }>;
  label: string;
  useHook: UseHook;
  optionsLabel?: string;
  valueId?: number;
  cacheKey?: string;
  onChange: (id: number | undefined, displayName?: string) => void;
  onDisplayResolved?: (id: number, displayName: string) => void;
  hookFilters?: Record<string, any>;
};

/**
 * Оборачивает RHF Dictionary для использования в фильтрах (valueId/onChange вместо react-hook-form).
 * Сохраняет кэш отображения и резолв по id при открытии.
 */
export function FilterDictionaryField({
  DictionaryComponent,
  label,
  useHook,
  optionsLabel = 'name',
  valueId,
  cacheKey = 'ref',
  onChange,
  onDisplayResolved,
  hookFilters = {},
}: FilterDictionaryFieldProps) {
  const hook = useHook(false);
  const loadItem = (hook as any).loadItem as ((id: number | string) => Promise<any>) | undefined;
  const loadItemRef = useRef(loadItem);
  loadItemRef.current = loadItem;
  const onDisplayResolvedRef = useRef(onDisplayResolved);
  onDisplayResolvedRef.current = onDisplayResolved;

  const methods = useForm<{ filterRef: any }>({
    defaultValues: { filterRef: null },
  });
  const { setValue } = methods;
  const watchedValue = useWatch({ control: methods.control, name: 'filterRef', defaultValue: null });
  const prevWatchedRef = useRef<any>(null);
  const isExternalUpdateRef = useRef(false);

  // Синхронизация: valueId (из фильтра) -> form value
  useEffect(() => {
    if (!valueId) {
      isExternalUpdateRef.current = true;
      setValue('filterRef', null);
      return;
    }
    const cacheEntry = displayCache.get(`${cacheKey}:${valueId}`);
    if (cacheEntry) {
      const obj = { id: cacheEntry.id, name: cacheEntry.name };
      isExternalUpdateRef.current = true;
      setValue('filterRef', obj);
      return;
    }
    if (!loadItemRef.current) {
      isExternalUpdateRef.current = true;
      setValue('filterRef', null);
      return;
    }
    loadItemRef.current(valueId).then((item: any) => {
      if (item?.id && item?.[optionsLabel] != null) {
        const displayName = String(item[optionsLabel]);
        const obj = { ...item };
        displayCache.set(`${cacheKey}:${valueId}`, { id: item.id, name: displayName });
        isExternalUpdateRef.current = true;
        setValue('filterRef', obj);
        onDisplayResolvedRef.current?.(item.id, displayName);
      }
    }).catch(() => {
      isExternalUpdateRef.current = true;
      setValue('filterRef', null);
    });
  }, [valueId, cacheKey, optionsLabel, setValue]);

  // Синхронизация: form value (выбор пользователя) -> onChange
  useEffect(() => {
    if (isExternalUpdateRef.current) {
      isExternalUpdateRef.current = false;
      prevWatchedRef.current = watchedValue;
      return;
    }
    if (prevWatchedRef.current === watchedValue) return;
    prevWatchedRef.current = watchedValue;
    const id = watchedValue?.id;
    const displayName = watchedValue?.[optionsLabel] ?? watchedValue?.name;
    if (watchedValue && id) {
      displayCache.set(`${cacheKey}:${id}`, { id, name: String(displayName ?? '') });
    }
    onChange(id, displayName != null ? String(displayName) : undefined);
  }, [watchedValue, onChange, optionsLabel, cacheKey]);

  return (
    <FormProvider {...methods}>
      <DictionaryComponent name="filterRef" label={label} />
    </FormProvider>
  );
}
